import React from 'react'
import { Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { DEFAULT_ROLE, ROLES_PER_PAGE, useMeetingUsers, UsersConsent } from './MeetingSummary/hooks/users'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { User } from '../../../models/User'
import { CONSENT_EXCLUDED_ROLES } from './MeetingSummary/ConfigureAssignment/AssignmentRoleNameMapping'
import { Button } from '../../../components/Button/Button'
import { useParams } from 'react-router-dom'
import { useGetRoomSessionQuery, useOverrideMeetingConsentMutation } from '../../../store/media/media'
import { useAccount } from '../../../common/useAccount'
import ConsentAvatarLarge from '../../../assets/images/consent-avatart-large.png'
import { ConsentAvatar } from '../../../components/Consent/ConsentAvatar'
import { getUserDisplayName } from './common/aifeatures/utils'
import { ConsentIcon } from '../../../components/Consent/ConsentIcon'
import './ConsentNotAvailable.scss'

export interface ConsentNotAvailableProps {
  loading: boolean
}

export const ConsentNotAvailable: React.FC<ConsentNotAvailableProps> = ({ loading }) => {
  const { account } = useAccount()
  const userConsentManualOverrideEnable = account?.effectiveConfig.complianceConfig.userConsentManualOverride || false

  if (loading) {
    return (
      <div className='vh-100'>
        <div className='skeleton w-100 h-25' />
      </div>
    )
  }

  return (
    <Container>
      <Row className='p-0'>
        <div className='text-center mt-5'>
          <img src={ConsentAvatarLarge} alt='Consent Avatar' style={{ width: '176px', height: '176px' }} />
        </div>
      </Row>
      <Row>
        <Col lg={8} className='mx-auto'>
          <div className='d-flex flex-column gap-3 justify-content-center align-items-center mt-3'>
            <h4>Verify participant consent</h4>
            <div className='text-center'>
              <span>
                Verify that all participants were notified and consented to this meeting being transcribed and analyzed by AI.
                {userConsentManualOverrideEnable ? ' Confirming will mark all participant consent as accepted.' : null}
              </span>
            </div>
            <ClientConsentParticipants userConsentManualOverrideEnable={userConsentManualOverrideEnable} />
          </div>
        </Col>
      </Row>
    </Container>
  )
}

interface ClientConsentParticipantsProps {
  userConsentManualOverrideEnable: boolean
}

const ClientConsentParticipants: React.FC<ClientConsentParticipantsProps> = ({ userConsentManualOverrideEnable }) => {
  const accountId = useAppSelector(selectAccountId)
  const { roomId = '', sessionId = '' } = useParams()

  const { data: roomSession } = useGetRoomSessionQuery({ accountId, roomId, sessionId })
  const [overrideMeetingConsent, { isLoading }] = useOverrideMeetingConsentMutation()
  const { usersByRoles, usersByRolesOnCurrentPage, usersConsent, pages, currentPage, nextPage, previousPage } = useMeetingUsers(roomSession?.data ?? null)

  const handleOverrideConsent = async () => {
    if (!roomSession?.data) {
      return
    }
    await overrideMeetingConsent({
      accountId,
      roomId,
      sessionId,
    })
  }

  const getClientsBody = (usersByRoles: { [key: string]: User[] }, usersConsent: UsersConsent | null) => {
    const clients = (usersByRoles[DEFAULT_ROLE] || []).filter((client) => !client.id.startsWith('speaker_'))

    return (
      <Col xs={12} md={12 / ROLES_PER_PAGE} className='d-flex flex-column p-0'>
        <div className='d-flex flex-row align-items-center participants-container'>
          {clients.map((client, index) => {
            const consent = usersConsent ? (client.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id)) ? usersConsent[client.id] : null) : null
            return (
              <div key={client.id} style={{ marginLeft: index > 0 ? '-11px' : '0', zIndex: clients.length - index, position: 'relative' }}>
                <ConsentAvatar name={client.name} consentState={consent?.current?.state} />
              </div>
            )
          })}
          {clients.length > 0 && (
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip className='consent-tooltip'>
                  <div className='bg-black text-white rounded p-2'>
                    <div className='d-flex flex-column gap-2'>
                      {clients.map((client) => {
                        const consent = usersConsent ? usersConsent[client.id] : null
                        const shouldShowConsent = client.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id))
                        return (
                          <div key={client.id} className='d-flex gap-2 align-items-center' style={{ whiteSpace: 'nowrap' }}>
                            <i className='bi bi-person-fill flex-shrink-0' />
                            <div className='flex-shrink-0'>{getUserDisplayName(client)}</div>
                            {shouldShowConsent && (
                              <div className='flex-shrink-0'>
                                <ConsentIcon contact={client} consent={consent} showText={true} />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </Tooltip>
              }
              popperConfig={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 20],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      enabled: false,
                    },
                  },
                ],
              }}
            >
              <Button variant='link' className='button-link-small ms-2 p-0'>
                Show all
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </Col>
    )
  }

  return (
    <Container className='zocks-pl-2.75 my-3 d-flex flex-column zocks-gap-2 w-100 mw-100 embedded-container'>
      <Row className='justify-content-center align-items-center zocks-border-color-gray-300 border border-1 rounded p-3'>
        <Col>
          <Row className='d-none d-lg-flex'>{getClientsBody(usersByRolesOnCurrentPage, usersConsent)}</Row>
          <Row className='d-flex d-lg-none'>{getClientsBody(usersByRoles, usersConsent)}</Row>
          {pages > 1 ? (
            <Row className='d-none d-lg-flex justify-content-end'>
              <Col className='p-0 d-flex zocks-gap-0.75 align-items-center' xs='auto'>
                <Button variant='link' className='page-button' disabled={currentPage === 1} onClick={previousPage}>
                  <i className='bi bi-chevron-left' />
                </Button>
                <div className='lead-extra-small'>
                  {currentPage}/{pages}
                </div>
                <Button variant='link' className='page-button' disabled={currentPage === pages} onClick={nextPage}>
                  <i className='bi bi-chevron-right' />
                </Button>
              </Col>
            </Row>
          ) : null}
        </Col>
        <Col xs='auto'>
          {userConsentManualOverrideEnable ? (
            <Button variant='primary' onClick={handleOverrideConsent} disabled={isLoading} loading={isLoading}>
              Confirm consent
            </Button>
          ) : null}
        </Col>
      </Row>
      <Row className='px-2 py-3 zocks-bg-color-gray-100 gap-3 justify-content-start border border-1 rounded'>
        <div className='d-flex align-self-center gap-3 align-items-center zocks-text-gray-700'>
          <i className='bi bi-exclamation-triangle' />
          <span>Unverified meetings and their data will be automatically deleted per data retention policy.</span>
        </div>
      </Row>
    </Container>
  )
}
